import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import qapi from 'views_shared/app/javascript/api';
import MessageComposer from './MessageComposer';
import { MessagesProvider } from './MessagesContext';

// First reply on a conversationless pitch: the REAL MessageComposer inside a provider whose
// sendOverride posts to pitch_replies (server find-or-creates the conversation + receipts);
// on success the parent refetches and the pane flips to the real thread.
function FirstSendComposer({
 pitch, request, conversationShape, onSent, sendButtonPortal = null, prefill = null,
}) {
  // Address by V2 id when the pitch has no migrate_id (V2-native), by migrate_id otherwise
  // — the endpoint keeps the two id spaces in separate params rather than guessing.
  const sendOverride = useCallback(
    async ({ body, attachments, videoRequest }) => {
      // V1-MIGRATION id-space bridge: the request page hands V1 pitch records (no
      // migrate_id KEY; its numeric request.id resolves as a V1 id server-side), while the
      // messaging inbox hands V2 API rows (migrate_id key always present, null when the
      // pitch is V2-native). The two id spaces overlap numerically, so this must branch.
      const isV2Row = 'migrate_id' in pitch;
      const result = await qapi.pitchReplies.send(isV2Row ? request.slug : request.id, {
        ...(isV2Row && !pitch.migrate_id
          ? { v2PitchIds: [pitch.id] }
          : { pitchIds: [isV2Row ? pitch.migrate_id : pitch.id] }),
        body,
        attachments,
        videoRequest,
      });
      if (!result.error) onSent();
      return result;
    },
    [request.slug, request.id, pitch, onSent],
  );

  return (
    <MessagesProvider
      conversationId={null}
      sendOverride={sendOverride}
      initialComposerState={
        prefill ? { messageContent: prefill.body, videoRequestIntent: !!prefill.videoRequest } : undefined
      }
    >
      <MessageComposer conversation={conversationShape} sendButtonPortal={sendButtonPortal} />
    </MessagesProvider>
  );
}

FirstSendComposer.propTypes = {
  pitch: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  request: PropTypes.shape({
    id: PropTypes.number,
    slug: PropTypes.string,
  }).isRequired,
  // Pitcher-as-sole-participant shape from PitchThreadPane (drives the composer placeholder)
  // eslint-disable-next-line react/forbid-prop-types -- conversation-shaped passthrough for my_inbox components
  conversationShape: PropTypes.object.isRequired,
  onSent: PropTypes.func.isRequired,
  sendButtonPortal: typeof Element === 'undefined' ? PropTypes.any : PropTypes.instanceOf(Element),
  prefill: PropTypes.shape({
    body: PropTypes.string,
    videoRequest: PropTypes.bool,
  }),
};

export default FirstSendComposer;
